<template>
	<div class="command-categories" style="height: 116px; min-height: 116px; max-height: 116px">
		<div v-for="category in isEnterEditingComponentStatus ? editComponentCategories : categories" :key="category.title" class="category category-feature">
			<div class="category-actions feature-layout-grid" :style="getFeatureGridStyle(category)" :data-feature-category="category.title">
				<div
					v-for="item in getFeatureLayoutItems(category)"
					:key="item.action.key"
					class="feature-command"
					:class="{
						active: isFeatureActionActive(item.action),
						'feature-command-large': item.size === 'large',
						'feature-command-small': item.size === 'small',
					}"
					:style="getFeatureCellStyle(item)"
					:data-assembly-key="item.action.key"
					:data-assembly-size="item.size"
					:data-assembly-mode="item.mode"
					:data-assembly-row-span="String(item.rowSpan)"
				>
					<DropdownButton
						v-if="item.action.key === 'assembly-transform'"
						:is-active="isAssemblyTransformActive"
						:disabled="Boolean(activeAssemblyTransformOption.disabled)"
						:icon="activeAssemblyTransformOption.icon"
						:label="activeAssemblyTransformOption.label"
						:hint="{
							image: activeAssemblyTransformOption.hintImage,
							desc: activeAssemblyTransformOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="assemblyTransformDropdownOptions"
						@select="handleAssemblyTransformSelect"
						@click="handleAssemblyTransformClick"
					/>

					<DropdownButton
						v-else-if="item.action.key === 'assembly-reference-geometry'"
						:is-active="isAssemblyReferenceActive"
						:disabled="Boolean(activeAssemblyReferenceOption.disabled)"
						:icon="activeAssemblyReferenceOption.icon"
						:label="activeAssemblyReferenceOption.label"
						:hint="{
							image: activeAssemblyReferenceOption.hintImage,
							desc: activeAssemblyReferenceOption.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="assemblyReferenceDropdownOptions"
						@select="handleAssemblyReferenceSelect"
						@click="handleAssemblyReferenceClick"
					/>

					<DropdownButton
						v-else-if="item.action.key === 'assembly-edit-component'"
						:is-active="isEditComponentActive"
						:disabled="isEditComponentDisabled"
						:icon="assemblyEditComponentIcon"
						:label="item.action.label"
						:hint="{
							image: item.action.hintImage,
							desc: item.action.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						@click="handleAction(item.action)"
					/>

					<DropdownButton
						v-else
						:is-active="isCommandActive(item.action.command)"
						:disabled="Boolean(item.action.disabled)"
						:icon="item.action.icon"
						:label="item.action.label"
						:hint="{
							image: item.action.hintImage,
							desc: item.action.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						@click="handleAction(item.action)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, h, onMounted, onUnmounted, ref, watch, type CSSProperties } from "vue";
	import { storeToRefs } from "pinia";
	import type { DropdownOption } from "naive-ui";
	import type { Subscription } from "rxjs";
	import DropdownButton from "@/components/common/DropdownButton.vue";
	import SvgIcon from "@/components/common/SvgIcon.vue";
	import assemblyEditComponentIcon from "@/assets/icon/assembly_edit_component.svg?raw";
	import assemblyInsertComponentIcon from "@/assets/icon/assembly_insert_component.svg?raw";
	import assemblyMateIcon from "@/assets/icon/assembly_mate.svg?raw";
	import assemblyMoveComponentIcon from "@/assets/icon/move.svg?raw";
	import assemblyRotateComponentIcon from "@/assets/icon/rotate.svg?raw";
	import assemblyLinearComponentPatternIcon from "@/assets/icon/feature_pattern_linear.svg?raw";
	import featureReferenceGeometryIcon from "@/assets/icon/feature_reference_geometry.svg?raw";
	import sketchSnapPointIcon from "@/assets/icon/sketch_snap_point.svg?raw";
	import axisIcon from "@/assets/icon/axis.svg?raw";
	import assemblyNoExternalReferenceIcon from "@/assets/icon/assembly_no_external_reference.svg?raw";
	import { useEditorAssemblyWorkflow } from "@/views/editor/useEditorAssemblyWorkflow";
	import { useToolbarCommandStore } from "@/stores/toolbar-command";
	import { AssemblyDragEventName, useEvent } from "@puppycad/cad-core";

	interface ToolbarAction {
		key: string;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	type FeatureLayoutCellSize = "small" | "large";
	type FeatureLayoutCellMode = "row" | "column";

	interface FeatureLayoutCell {
		key: string;
		column: number;
		row: number;
		rowSpan?: number;
		size?: FeatureLayoutCellSize;
		mode?: FeatureLayoutCellMode;
	}

	interface FeatureLayout {
		columns: number;
		rows: number;
		cells: FeatureLayoutCell[];
	}

	interface FeatureLayoutItem {
		action: ToolbarAction;
		column: number;
		row: number;
		rowSpan: number;
		size: FeatureLayoutCellSize;
		mode: FeatureLayoutCellMode;
	}

	interface ToolbarCategory {
		title: string;
		actions: ToolbarAction[];
		featureLayout: FeatureLayout;
	}

	interface ToolbarOperation {
		name: string;
		hint: string;
	}

	type AssemblyTransformModeKey = "assembly-move-component" | "assembly-rotate-component";
	type AssemblyReferenceModeKey = "assembly-reference-plane" | "assembly-reference-axis" | "assembly-reference-point";

	interface AssemblyTransformModeOption {
		mode: AssemblyTransformModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface AssemblyReferenceModeOption {
		mode: AssemblyReferenceModeKey;
		label: string;
		icon: string;
		command: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	const props = withDefaults(
		defineProps<{
			assemblyEditComponentDisabled: boolean;
		}>(),
		{
			assemblyEditComponentDisabled: true,
		}
	);

	const emit = defineEmits<{
		(e: "command", commandType: string): void;
		(e: "operationChange", operation: ToolbarOperation): void;
	}>();

	const toolbarCommandStore = useToolbarCommandStore();
	const { activeCommands } = storeToRefs(toolbarCommandStore);

	//------ 装配体移动旋转入口 开始------
	const assemblyTransformModeOptions: AssemblyTransformModeOption[] = [
		{
			mode: "assembly-move-component",
			label: "移动零部件",
			icon: assemblyMoveComponentIcon,
			command: "assembly-move-component",
			hint: "在由其配合所定义的自由度内移动零部件。",
			hintImage: "/images/operation/assembly-move-component.gif",
		},
		{
			mode: "assembly-rotate-component",
			label: "旋转零部件",
			icon: assemblyRotateComponentIcon,
			command: "assembly-rotate-component",
			hint: "在由其配合所定义的自由度内旋转零部件。",
			hintImage: "/images/operation/assembly-rotate-component.gif",
		},
	];

	const assemblyTransformModeMap = new Map(assemblyTransformModeOptions.map(option => [option.mode, option]));
	const assemblyTransformCommandMap = new Map(assemblyTransformModeOptions.map(option => [option.command, option]));
	const assemblyTransformCommandSet = new Set(assemblyTransformModeOptions.map(option => option.command));
	const activeAssemblyTransformMode = ref<AssemblyTransformModeKey>("assembly-move-component");
	const activeAssemblyTransformOption = computed(() => assemblyTransformModeMap.get(activeAssemblyTransformMode.value) ?? assemblyTransformModeOptions[0]);
	const isAssemblyTransformActive = computed(
		() =>
			// 工具命令激活 OR 正在直接拖拽零部件时，均高亮 assembly-transform tab
			activeCommands.value.some(command => assemblyTransformCommandSet.has(command)) || isDraggingAssemblyPart.value
	);
	const assemblyTransformDropdownOptions = computed<DropdownOption[]>(() =>
		assemblyTransformModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);
	//------ 装配体移动旋转入口 结束------

	//------ 装配体参考几何体入口 开始------
	const assemblyReferenceModeOptions: AssemblyReferenceModeOption[] = [
		{
			mode: "assembly-reference-point",
			label: "基准点",
			icon: sketchSnapPointIcon,
			command: "assembly-reference-point",
			hint: "创建装配体参考基准点",
		},
		{
			mode: "assembly-reference-axis",
			label: "基准轴",
			icon: axisIcon,
			command: "assembly-reference-axis",
			hint: "创建装配体参考基准轴",
		},
		{
			mode: "assembly-reference-plane",
			label: "基准面",
			icon: featureReferenceGeometryIcon,
			command: "assembly-reference-plane",
			hint: "创建装配体参考基准面",
		},
	];

	const assemblyReferenceModeMap = new Map(assemblyReferenceModeOptions.map(option => [option.mode, option]));
	const assemblyReferenceCommandMap = new Map(assemblyReferenceModeOptions.map(option => [option.command, option]));
	const assemblyReferenceCommandSet = new Set(assemblyReferenceModeOptions.map(option => option.command));
	const activeAssemblyReferenceMode = ref<AssemblyReferenceModeKey>("assembly-reference-plane");
	const activeAssemblyReferenceOption = computed(() => assemblyReferenceModeMap.get(activeAssemblyReferenceMode.value) ?? assemblyReferenceModeOptions[0]);
	const isAssemblyReferenceActive = computed(() => activeCommands.value.some(command => assemblyReferenceCommandSet.has(command)));
	const assemblyReferenceDropdownOptions = computed<DropdownOption[]>(() =>
		assemblyReferenceModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);
	//------ 装配体参考几何体入口 结束------

	//------ 装配体零部件拖拽高亮联动 开始------
	/**
	 * 当前是否有零部件正在被直接拖拽（非通过命令激活）。
	 * 拖拽期间临时高亮 assembly-transform tab，松手后恢复。
	 */
	const isDraggingAssemblyPart = ref(false);
	//------ 装配体零部件拖拽高亮联动 结束------

	const categories: ToolbarCategory[] = [
		{
			title: "装配体",
			actions: [
				{
					key: "assembly-edit-component",
					label: "编辑零部件",
					icon: assemblyEditComponentIcon,
					command: "assembly-edit-component",
					hint: "在编辑顶级装配体和选定零件或子装配体之间切换。",
				},
				{
					key: "assembly-insert-component",
					label: "插入零部件",
					icon: assemblyInsertComponentIcon,
					command: "assembly-insert-component",
					hint: "向装配体中插入新的零部件",
					hintImage: "/images/operation/assembly-insert-component.gif",
				},
				{
					key: "assembly-mate",
					label: "配合",
					icon: assemblyMateIcon,
					command: "assembly-mate",
					hint: "创建零部件之间的几何图形关系，以定义零部件的线性或旋转运动的允许方向。",
					hintImage: "/images/operation/assembly-mate.gif",
				},
				//------ 线性零部件阵列入口 开始------
				{
					key: "assembly-linear-component-pattern",
					label: "线性零部件阵列",
					icon: assemblyLinearComponentPatternIcon,
					command: "assembly-linear-component-pattern",
					hint: "在一个或两个线性方向上阵列零部件。要定义阵列方向，请选择线性边线或尺寸。您可以指定选项以定义实例的间距、数量和方向。",
				},
				//------ 线性零部件阵列入口 结束------
				{
					key: "assembly-transform",
					label: "移动零部件",
					icon: assemblyMoveComponentIcon,
					command: "assembly-move-component",
					hint: "在由其配合所定义的自由度内移动零部件。",
				},
				{
					key: "assembly-reference-geometry",
					label: "基准面",
					icon: featureReferenceGeometryIcon,
					command: "assembly-reference-plane",
					hint: "创建装配体参考基准面",
				},
			],
			featureLayout: {
				columns: 6,
				rows: 3,
				cells: [
					{ key: "assembly-edit-component", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-insert-component", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-mate", column: 3, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-linear-component-pattern", column: 4, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-transform", column: 5, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-reference-geometry", column: 6, row: 1, rowSpan: 3, size: "large", mode: "column" },
				],
			},
		},
	];

	const editComponentCategories: ToolbarCategory[] = [
		{
			title: "编辑",
			actions: [
				{
					key: "assembly-edit-component",
					label: "编辑零部件",
					icon: assemblyEditComponentIcon,
					command: "assembly-edit-component",
					hint: "在编辑顶级装配体和选定零件或子装配体之间切换。",
				},
				{
					key: "assembly-no-external-reference",
					label: "无外部参考",
					icon: assemblyNoExternalReferenceIcon,
					command: "assembly-no-external-reference",
					hint: "在上下文中创建或编辑特征时，阻止创建外部参考。",
				},
				{
					key: "assembly-hide-show-components",
					label: "隐藏/显示零部件",
					icon: assemblyEditComponentIcon, // 临时使用编辑零部件图标
					command: "assembly-hide-show-components",
					hint: "隐藏或显示所选零部件。",
				},
				{
					key: "assembly-change-transparency",
					label: "更改透明度",
					icon: assemblyEditComponentIcon, // 临时使用编辑零部件图标
					command: "assembly-change-transparency",
					hint: "在0%和75之间切换所选零部件的透明度。",
				},
				{
					key: "assembly-transparency",
					label: "装配体透明度",
					icon: assemblyEditComponentIcon, // 临时使用编辑零部件图标
					command: "assembly-transparency",
					hint:"设置在装配体上下文中未编辑的零部件的透明度。透明度选项包括'不透明'、'保持透明'或'迫使透明'。编辑过的零部件仍然不透明。",
				},
			],
			featureLayout: {
				columns: 3,
				rows: 3,
				cells: [
					{ key: "assembly-edit-component", column: 1, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-no-external-reference", column: 2, row: 1, rowSpan: 3, size: "large", mode: "column" },
					{ key: "assembly-hide-show-components", column: 3, row: 1, size: "small", mode: "row" },
					{ key: "assembly-change-transparency", column: 3, row: 2, size: "small", mode: "row" },
					{ key: "assembly-transparency", column: 3, row: 3, size: "small", mode: "row" },
				],
			},
		},
	];

	function getFeatureGridStyle(category: ToolbarCategory): CSSProperties {
		return {
			"--edit-component-grid-columns": String(category.featureLayout.columns),
			"--edit-component-grid-rows": String(category.featureLayout.rows),
		} as CSSProperties;
	}

	function getFeatureLayoutItems(category: ToolbarCategory): FeatureLayoutItem[] {
		return category.featureLayout.cells
			.map(cell => {
				const action = category.actions.find(candidate => candidate.key === cell.key);
				if (!action) {
					return null;
				}
				return {
					action,
					column: cell.column,
					row: cell.row,
					rowSpan: cell.rowSpan ?? 1,
					size: cell.size ?? "small",
					mode: cell.mode ?? "column",
				} satisfies FeatureLayoutItem;
			})
			.filter((item): item is FeatureLayoutItem => item !== null);
	}

	function isFeatureActionActive(action: ToolbarAction): boolean {
		if (action.key === "assembly-edit-component") {
			return !props.assemblyEditComponentDisabled;
		}
		return isCommandActive(action.command);
	}

	function getFeatureCellStyle(item: FeatureLayoutItem): CSSProperties {
		return {
			gridColumn: String(item.column),
			gridRow: `${item.row} / span ${item.rowSpan}`,
		};
	}

	// ------ 编辑零部件入口 开始------
	const assemblyWorkflow = useEditorAssemblyWorkflow();
	const { isEnterEditingComponentStatus } = storeToRefs(assemblyWorkflow);
	const isEditComponentActive = computed(() => assemblyWorkflow.isEnterEditingComponentStatus);
	const isEditComponentDisabled = computed(() => (assemblyWorkflow.isEnterEditingComponentStatus ? false : props.assemblyEditComponentDisabled));

	// ------ 编辑零部件入口 结束------

	const gridStyle: CSSProperties = {
		"--feature-grid-columns": "6",
		"--feature-grid-rows": "2",
	} as CSSProperties;

	watch(
		activeCommands,
		commands => {
			const matched = commands.find(command => assemblyTransformCommandSet.has(command));
			if (!matched) {
				return;
			}
			const option = assemblyTransformCommandMap.get(matched);
			if (option) {
				activeAssemblyTransformMode.value = option.mode;
			}
		},
		{ deep: true, immediate: true }
	);

	watch(
		activeCommands,
		commands => {
			const matched = commands.find(command => assemblyReferenceCommandSet.has(command));
			if (!matched) {
				return;
			}
			const option = assemblyReferenceCommandMap.get(matched);
			if (option) {
				activeAssemblyReferenceMode.value = option.mode;
			}
		},
		{ deep: true, immediate: true }
	);

	/**
	 * 判断指定命令是否处于激活状态。
	 *
	 * @param command 编辑器当前要匹配的命令名。
	 * @returns 命令是否已在激活命令集合中。
	 */
	function isCommandActive(command: string): boolean {
		return activeCommands.value.includes(command);
	}

	//------ 装配体移动旋转入口 开始------
	/**
	 * 统一处理装配体移动/旋转下拉入口的命令发射与当前模式回显。
	 *
	 * @param command 需要激活的装配体变换命令。
	 */
	function applyAssemblyTransformCommand(command: string): void {
		const option = assemblyTransformCommandMap.get(command);
		if (!option || option.disabled) {
			return;
		}

		activeAssemblyTransformMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", {
			name: option.label,
			hint: option.hint,
		});
	}

	/**
	 * 处理装配体移动/旋转下拉选项切换。
	 *
	 * @param key 下拉项对应的命令键。
	 */
	function handleAssemblyTransformSelect(key: string): void {
		applyAssemblyTransformCommand(key);
	}

	/**
	 * 处理装配体移动/旋转主按钮点击，重复执行当前激活模式。
	 */
	function handleAssemblyTransformClick(): void {
		applyAssemblyTransformCommand(activeAssemblyTransformOption.value.command);
	}
	//------ 装配体移动旋转入口 结束------

	//------ 装配体参考几何体入口 开始------
	/**
	 * 统一处理装配体参考几何体下拉入口的命令发射与当前模式回显。
	 *
	 * @param command 需要激活的装配体参考几何体命令。
	 */
	function applyAssemblyReferenceCommand(command: string): void {
		const option = assemblyReferenceCommandMap.get(command);
		if (!option || option.disabled) {
			return;
		}

		activeAssemblyReferenceMode.value = option.mode;
		emit("command", option.command);
		emit("operationChange", {
			name: `装配体${option.label}`,
			hint: option.hint,
		});
	}

	/**
	 * 处理装配体参考几何体下拉选项切换。
	 *
	 * @param key 下拉项对应的命令键。
	 */
	function handleAssemblyReferenceSelect(key: string): void {
		applyAssemblyReferenceCommand(key);
	}

	/**
	 * 处理装配体参考几何体主按钮点击，重复执行当前激活模式。
	 */
	function handleAssemblyReferenceClick(): void {
		applyAssemblyReferenceCommand(activeAssemblyReferenceOption.value.command);
	}
	//------ 装配体参考几何体入口 结束------

	/**
	 * 处理普通装配体工具栏按钮点击。
	 *
	 * @param action 被点击的工具栏动作。
	 */
	function handleAction(action: ToolbarAction): void {
		if (action.disabled) return;
		emit("command", action.command);
		emit("operationChange", {
			name: action.label,
			hint: action.hint,
		});
	}

	//------ 装配体零部件拖拽高亮联动 开始------
	/** 拖拽事件订阅引用，组件卸载时统一释放。 */
	let dragStartedSubscription: Subscription | null = null;
	let dragStoppedSubscription: Subscription | null = null;

	const { add: addEvent, remove: removeEvent } = useEvent();

	onMounted(() => {
		/**
		 * 订阅拖拽开始事件，将 isDraggingAssemblyPart 置为 true。
		 * 无需关注具体被拖拽对象，只要有拖拽发生就高亮 assembly-transform tab。
		 */
		dragStartedSubscription = addEvent(AssemblyDragEventName.DragStarted, () => {
			isDraggingAssemblyPart.value = true;
		});

		/**
		 * 订阅拖拽停止事件，将 isDraggingAssemblyPart 置为 false，取消高亮。
		 */
		dragStoppedSubscription = addEvent(AssemblyDragEventName.DragStopped, () => {
			isDraggingAssemblyPart.value = false;
		});
	});

	onUnmounted(() => {
		// 组件销毁时释放 RxJS 订阅，防止内存泄漏。
		if (dragStartedSubscription) {
			removeEvent(AssemblyDragEventName.DragStarted, dragStartedSubscription);
			dragStartedSubscription = null;
		}
		if (dragStoppedSubscription) {
			removeEvent(AssemblyDragEventName.DragStopped, dragStoppedSubscription);
			dragStoppedSubscription = null;
		}
	});
	//------ 装配体零部件拖拽高亮联动 结束------
</script>

<style scoped lang="less">
	.command-categories {
		display: flex;
		align-items: stretch;
		gap: 0;
		flex-wrap: nowrap;
		// width: 100%;
		width: max-content;
		height: 116px;
		min-height: 116px;
		max-height: 116px;
		box-sizing: border-box;
		/**
   * 仅允许横向滚动展示更多按钮。
   * 若只写 overflow-x: auto、不写 overflow-y，规范会把 overflow-y 视为 auto，
   * 在垂直方向略超出时会出现右侧纵向滚动条，故显式隐藏纵向溢出。
   */
		overflow-x: hidden;
		overflow-y: hidden;

		border-right: 1px solid var(--n-tab-border-color);
	}

	.category {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 8px;
		min-width: max-content;
		position: relative;
	}

	.category-actions.feature-layout-grid {
		--feature-large-width: 66px;
		--feature-small-min-width: 62px;
		--feature-row-height: 34px;
		display: grid;
		grid-template-columns: repeat(var(--feature-grid-columns), max-content);
		grid-template-rows: repeat(var(--feature-grid-rows), var(--feature-row-height));
		align-items: stretch;
		justify-items: stretch;
		column-gap: 3px;
		row-gap: 3px;
	}

	.feature-command {
		width: max-content;
		height: 100%;
	}

	.feature-command-large {
		width: var(--feature-large-width);
	}

	.feature-command-large[data-assembly-key="assembly-transform"] {
		width: 74px;
	}

	.feature-command-small {
		width: max-content;
		min-width: var(--feature-small-min-width);
	}

	.feature-command > * {
		width: 100%;
	}

	:deep(.dropdown-option-icon) {
		width: 18px;
		height: 18px;
		object-fit: contain;
	}
</style>
